import { IconButton, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { useState, useCallback } from 'react';
import { MdViewHeadline, MdViewStream } from 'react-icons/md';
import { translate } from '../i18n';
import { TableDensity, getTableDensity, setTableDensity } from '../theme/chart-config';

interface DensityToggleProps {
	/** Unique table ID for persisting density preference */
	tableId: string;
	/** Callback when density changes */
	onChange?: (density: TableDensity) => void;
}

/**
 * Toggle between comfortable (52px rows) and dense (38px rows) table density.
 * Persists choice per-table in localStorage.
 */
export function DensityToggle({ tableId, onChange }: DensityToggleProps) {
	const [density, setDensityState] = useState<TableDensity>(() => getTableDensity(tableId));
	const iconColor = useColorModeValue('secondaryGray.600', 'gray.400');

	const toggle = useCallback(() => {
		const next: TableDensity = density === 'comfortable' ? 'dense' : 'comfortable';
		setDensityState(next);
		setTableDensity(tableId, next);
		onChange?.(next);
	}, [density, tableId, onChange]);

	return (
		<Tooltip label={density === 'comfortable' ? translate('court_pulse.compact_view') : translate('court_pulse.comfortable_view')} placement="top">
			<IconButton
				aria-label={`Switch to ${density === 'comfortable' ? 'dense' : 'comfortable'} view`}
				icon={density === 'comfortable' ? <MdViewHeadline /> : <MdViewStream />}
				variant="ghost"
				size="sm"
				color={iconColor}
				onClick={toggle}
			/>
		</Tooltip>
	);
}

/** Hook to get current density for a table, with re-render on change */
export function useTableDensity(tableId: string) {
	const [density, setDensityState] = useState<TableDensity>(() => getTableDensity(tableId));

	const onDensityChange = useCallback((next: TableDensity) => {
		setDensityState(next);
	}, []);

	return { density, onDensityChange };
}
